import { useEffect, useRef, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { callPost } from '@/api/fetcher';
import useDebounce from '@/hooks/useDebounce';
import {
  OrchestratorVoiceProvidersResponse,
  VOICE_PROVIDERS_ENDPOINT,
} from '@/hooks/useVoiceProviders';
import { selectIntegrationId } from '@/redux/session/selectors';
import { isApiKeyMasked } from '@/util/constants';

export type VerifyStatus = 'unverified' | 'validating' | 'verified' | 'invalid';

interface VerifyResponse {
  valid: boolean;
  voices: Array<{
    id: string;
    name: string;
    language?: string;
    labels?: Record<string, string>;
    preview_url?: string;
  }>;
}

const VERIFY_DEBOUNCE_MS = 1000;

/**
 * The integration's own ElevenLabs key: the toggle that adds it, and its verification, which
 * also swaps the cached voice list for the voices of that key.
 */
export const useElevenLabsKey = (basePath: string) => {
  const { control, setValue } = useFormContext();
  const apiKey: string | null | undefined = useWatch({ control, name: `${basePath}.api_key` });
  const region: string | undefined = useWatch({ control, name: `${basePath}.region` });
  const integrationId = useSelector(selectIntegrationId);
  const queryClient = useQueryClient();
  const debouncedApiKey = useDebounce(apiKey, VERIFY_DEBOUNCE_MS);
  const [verifyStatus, setVerifyStatus] = useState<VerifyStatus>('unverified');
  const verifyGenRef = useRef(0);

  const isByoEnabled = apiKey != null;
  const isKeyMasked = isApiKeyMasked(apiKey);

  const toggleByo = (checked: boolean) => {
    setValue(`${basePath}.api_key`, checked ? '' : null, { shouldDirty: true });
    setValue(`${basePath}.region`, checked ? 'global' : undefined, { shouldDirty: true });
  };

  const { mutateAsync: verify } = useMutation<
    VerifyResponse,
    Error,
    { api_key: string; region?: string }
  >({
    mutationFn: (params) =>
      callPost('/www/api/v1/integrations/voice/elevenlabs/verify-key', params),
  });

  useEffect(() => {
    if (!debouncedApiKey || isKeyMasked) {
      setVerifyStatus(isKeyMasked ? 'verified' : 'unverified');
      return;
    }
    const gen = ++verifyGenRef.current;
    setVerifyStatus('validating');
    verify({ api_key: debouncedApiKey, region })
      .then((data) => {
        if (gen !== verifyGenRef.current) {
          return;
        }
        setVerifyStatus('verified');
        queryClient.setQueryData<OrchestratorVoiceProvidersResponse>(
          [VOICE_PROVIDERS_ENDPOINT, integrationId],
          (old) => {
            if (!old?.tts) {
              return old;
            }
            return {
              ...old,
              tts: {
                providers: old.tts.providers.map((p) =>
                  p.id === 'elevenlabs' ? { ...p, voices: data.voices } : p
                ),
              },
            };
          }
        );
      })
      .catch(() => {
        if (gen === verifyGenRef.current) {
          setVerifyStatus('invalid');
        }
      });
    return () => {
      // A newer key makes the in-flight answer stale
      // eslint-disable-next-line react-hooks/exhaustive-deps
      verifyGenRef.current++;
    };
    // verify is excluded to avoid a re-trigger loop (same pattern as useTwilioVoice)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedApiKey, isKeyMasked, region]);

  return { isByoEnabled, verifyStatus, toggleByo };
};
